import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
// import { useAuth } from './contexts/AuthContext';
import { useAuth } from './contexts/KeycloakAuthContext';
import Layout from './components/Layout';
import Login from './components/Login';
import SignInScreen from 'shared/signin/SignInScreen';
import { resetRedirects } from 'shared/signin/signInRedirect';
import SignedOutScreen from 'shared/signin/SignedOutScreen';
import { isSignedOutPath, isSigningOut } from 'shared/signin/signOut';

const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Tenants = React.lazy(() => import('./pages/Tenants'));
const TenantDetail = React.lazy(() => import('./pages/TenantDetail'));
const Showrooms = React.lazy(() => import('./pages/Showrooms'));
const ShowroomDetail = React.lazy(() => import('./pages/ShowroomDetail'));
const Mqtt = React.lazy(() => import('./pages/Mqtt'));
const MqttBroker = React.lazy(() => import('./pages/MqttBroker'));
const Analytics = React.lazy(() => import('./pages/Analytics'));
const AccessControl = React.lazy(() => import('./pages/AccessControl'));
const Releases = React.lazy(() => import('./pages/Releases'));
const AiMirra = React.lazy(() => import('./pages/AiMirra'));

const Fallback = (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
    <CircularProgress />
  </Box>
);

/**
 * Route guard: renders children only if the user holds ANY of `anyOf`.
 * Otherwise bounces to the dashboard (always accessible). Mirrors the nav
 * filtering in Layout so a directly-typed URL can't bypass hidden menus.
 */
function Require({ anyOf, children }) {
  const { hasPermission } = useAuth();
  const ok = !anyOf || anyOf.some((p) => hasPermission(p));
  return ok ? children : <Navigate to="/dashboard" replace />;
}

export function PermissionHydrationGuard({ children }) {
  const { permissionsStatus, permissionsError, retryPermissions, logout } = useAuth();
  // Keep the workspace mounted during a transient background outage, so forms
  // retain their local state. The API still checks every action server-side.
  if (permissionsStatus === 'ready') return <>
    {permissionsError && <Alert severity="warning" action={<Button color="inherit" onClick={retryPermissions}>Retry</Button>}>{permissionsError}</Alert>}
    {children}
  </>;
  return <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 3 }}>
    <Stack spacing={2} sx={{ width: '100%', maxWidth: 540 }}>
      <Typography variant="h5">24K.Infinity</Typography>
      {permissionsStatus === 'error' ? <>
        <Alert severity="error">{permissionsError}</Alert>
        <Stack direction="row" spacing={1}><Button variant="contained" onClick={retryPermissions}>Retry loading access</Button><Button onClick={logout}>Sign out</Button></Stack>
      </> : <Stack direction="row" spacing={2} alignItems="center" role="status"><CircularProgress size={24} /><Typography>Loading your workspace access…</Typography></Stack>}
    </Stack>
  </Box>;
}

function App() {
  const { loading, isAuthenticated, signInAgain } = useAuth();
  const { pathname } = useLocation();
  // A signed-in return ends the sign-in redirect loop guard.
  useEffect(() => { if (isAuthenticated) resetRedirects(); }, [isAuthenticated]);

  // After sign-out: the branded confirmation, never an automatic sign-in.
  if (isSignedOutPath(pathname)) {
    return <SignedOutScreen product="infinity" context="Provider control plane" status={isSigningOut() ? 'signing-out' : 'signed-out'} onSignIn={signInAgain} />;
  }

  // The identity client is starting, and sends a signed-out visitor to the sign-in page.
  if (loading) return <SignInScreen product="infinity" context="Provider control plane" />;

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <PermissionHydrationGuard><Layout>
      <Suspense fallback={Fallback}>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/tenants" element={<Require anyOf={['tenants.read', 'tenants.manage']}><Tenants /></Require>} />
          {/* Create is now a modal on the tenants list; keep the old path redirecting. */}
          <Route path="/tenants/new" element={<Navigate to="/tenants" replace />} />
          <Route path="/tenants/:slug" element={<Require anyOf={['tenants.read', 'tenants.manage']}><TenantDetail /></Require>} />
          <Route path="/showrooms" element={<Require anyOf={['showrooms.manage']}><Showrooms /></Require>} />
          <Route path="/showrooms/:slug" element={<Require anyOf={['showrooms.manage']}><ShowroomDetail /></Require>} />
          {/* Catalog and Deployments were retired in the 2026-08 rework; redirect old bookmarks. */}
          <Route path="/catalog" element={<Navigate to="/dashboard" replace />} />
          <Route path="/deployments" element={<Navigate to="/dashboard" replace />} />
          <Route path="/mqtt" element={<Require anyOf={['mqtt.manage', 'mqtt.read']}><Mqtt /></Require>} />
          <Route path="/mqtt/:slug" element={<Require anyOf={['mqtt.manage', 'mqtt.read']}><MqttBroker /></Require>} />
          <Route path="/analytics" element={<Require anyOf={['analytics.read']}><Analytics /></Require>} />
          <Route path="/ai" element={<Require anyOf={['ai.read']}><AiMirra /></Require>} />
          <Route path="/releases" element={<Require anyOf={['releases.read', 'releases.manage', 'releases.promote']}><Releases /></Require>} />
          <Route path="/access-control" element={<Require anyOf={['users.read', 'users.manage', 'roles.read', 'roles.manage', 'analytics.read']}><AccessControl /></Require>} />
          <Route path="/users" element={<Navigate to="/access-control?tab=users" replace />} />
          <Route path="/roles" element={<Navigate to="/access-control?tab=roles" replace />} />
          {/* The design-system showcase is an internal implementation reference, not a provider module. */}
          <Route path="/design-system" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </Layout></PermissionHydrationGuard>
  );
}

export default App;
